import * as React from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Modal, Button, Stack, Typography,
  Divider, Alert, Chip, TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '45vw',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
  overflowY: 'auto',
  backgroundColor: '#f5f5f5',
};

const StatusItem = ({ label, ok }) => (
  <Stack direction="row" alignItems="center" gap={1} py={0.5}>
    {ok
      ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
      : <WarningAmberIcon sx={{ color: '#e65100', fontSize: 20 }} />}
    <Typography variant="body2" color={ok ? '#2e7d32' : '#e65100'}>
      {label}
    </Typography>
    {ok
      ? <Chip label="OK" size="small" color="success" sx={{ ml: 'auto' }} />
      : <Chip label="Pendente" size="small" color="warning" sx={{ ml: 'auto' }} />}
  </Stack>
);

export default function ModalFormularioMontagem({ open, onClose, anexo, onAddAnexo, proposta, setProposta }) {
  const [valorMontagem, setValorMontagem] = useState(proposta?.valormontagem || '');

  React.useEffect(() => {
    if (proposta?.valormontagem) setValorMontagem(proposta.valormontagem);
  }, [proposta?.valormontagem]);

  const salvarValor = () => {
    setProposta((prev) => ({ ...prev, valormontagem: parseFloat(valorMontagem) || 0 }));
  };

  const temValor = Number(valorMontagem) > 0;
  const temNF = React.useMemo(
    () => anexo?.some((a) => a.tipo?.nome === 'Nota Fiscal de Montagem'),
    [anexo]
  );
  const temFoto = React.useMemo(
    () => anexo?.some((a) => a.tipo?.nome === 'Foto de Montagem'),
    [anexo]
  );

  const tudo = temValor && temNF && temFoto;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={2}>
          Formulário de Montagem — Pendências
        </Typography>

        <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
          Adicione a Nota Fiscal e ao menos uma Foto de Montagem para avançar para a próxima etapa.
          Ambos os itens são obrigatórios.
        </Alert>

        {/* Status geral */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" mb={1}>Status</Typography>
          <StatusItem label="Valor do serviço informado" ok={temValor} />
          <Divider />
          <StatusItem label="Nota Fiscal de Montagem anexada" ok={temNF} />
          <Divider />
          <StatusItem label="Foto de Montagem anexada" ok={temFoto} />
        </Box>

        {/* Valor do Serviço de Montagem */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="#1565C0" mb={1}>
            1. Valor do Serviço de Montagem
          </Typography>
          <Stack direction="row" gap={2} alignItems="center">
            <TextField
              size="small"
              label="Valor (R$)"
              type="number"
              value={valorMontagem}
              onChange={(e) => setValorMontagem(e.target.value)}
              onBlur={salvarValor}
              sx={{ width: 200, backgroundColor: 'white' }}
              InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>R$</span> }}
            />
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={salvarValor}
              disabled={!valorMontagem || valorMontagem <= 0}
            >
              Salvar Valor
            </Button>
          </Stack>
        </Box>

        {/* NF de Montagem */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="#1565C0" mb={1}>
            2. Nota Fiscal de Montagem
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Selecione o tipo "Nota Fiscal de Montagem" ao anexar. Visível apenas para supervisor e quem anexou.
          </Typography>
          {temNF
            ? <Chip label="NF já anexada ✓" color="success" size="small" />
            : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => onAddAnexo('nf_montagem')}
                sx={{ borderColor: '#e65100', color: '#e65100' }}
              >
                Adicionar Nota Fiscal
              </Button>
            )}
        </Box>

        {/* Foto de Montagem */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="#1565C0" mb={1}>
            3. Fotos da Montagem
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Adicione ao menos uma foto da instalação concluída.
          </Typography>
          {temFoto
            ? <Chip label="Foto já anexada ✓" color="success" size="small" />
            : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => onAddAnexo('foto_montagem')}
                sx={{ borderColor: '#1565C0', color: '#1565C0' }}
              >
                Adicionar Foto
              </Button>
            )}
          {/* Permite adicionar mais fotos mesmo após a primeira */}
          {temFoto && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => onAddAnexo('foto_montagem')}
              sx={{ borderColor: '#1565C0', color: '#1565C0', ml: 2 }}
            >
              Adicionar mais fotos
            </Button>
          )}
        </Box>

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={onClose}
            color={tudo ? 'success' : 'inherit'}
          >
            {tudo ? 'Concluído ✓' : 'Fechar'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

ModalFormularioMontagem.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  anexo: PropTypes.array,
  onAddAnexo: PropTypes.func,
  proposta: PropTypes.object,
  setProposta: PropTypes.func,
};